import { Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Segment } from '../../types/segment.type';
import { useSegmentEditForm } from '../../hooks/use-segment-edit-form.hook';
import { SegmentInformationsCard } from './SegmentInformationsCard.component';
import { SegmentBillingCard } from './SegmentBillingCard.component';

type Props = {
  segment: Segment;
  isUpdating: boolean;
  updateSegment: (editedFields: Partial<Segment>) => Promise<void>;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& h4': {
      margin: 0,
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  actionsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  body: {
    display: 'flex',
    gap: '2rem',
  },
  column: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& > section': {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
  },
}));

export default function SegmentInformationsTab({ segment, isUpdating, updateSegment }: Props) {
  const classes = useStyles();
  const {
    isEditing,
    updatedSegment,
    addressLabel,
    isPlaceSelected,
    isZipCodeInvalid,
    isBillingAddressIncomplete,
    isNameInvalid,
    isRateInvalid,
    handleChange,
    handleAddressChange,
    handleAddressLabelChange,
    handleAddressSelected,
    handleCancelEditing,
    handleSave,
  } = useSegmentEditForm(segment);

  return (
    <section className={classes.root}>
      <div className={classes.actionsHeader}>
        <Button size="small" variant="text" onClick={handleCancelEditing} disabled={!isEditing || isUpdating}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          onClick={() => handleSave(updateSegment)}
          disabled={!isEditing || isUpdating || isBillingAddressIncomplete || isNameInvalid || isRateInvalid}
          startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
      <section className={classes.body}>
        <div className={classes.column}>
          <SegmentInformationsCard
            updatedSegment={updatedSegment}
            isArchived={Boolean(segment.archivedAt)}
            isNameInvalid={isNameInvalid}
            isRateInvalid={isRateInvalid}
            onFieldChange={handleChange}
          />
        </div>
        <div className={classes.column}>
          <SegmentBillingCard
            updatedSegment={updatedSegment}
            addressLabel={addressLabel}
            isPlaceSelected={isPlaceSelected}
            isZipCodeInvalid={isZipCodeInvalid}
            onAddressLabelChange={handleAddressLabelChange}
            onAddressSelected={handleAddressSelected}
            onAddressFieldChange={handleAddressChange}
            onFieldChange={handleChange}
          />
        </div>
      </section>
    </section>
  );
}
